import { useState } from "react";
import api from "services/api";

import { IActivity, ITemplate, IWorkspace } from "./types";
import { ActivitySetupConfig } from "./ActivitySetupConfig";
import { Bttn, Title } from "./styled";
import { Button, IconButton, Tab, Tabs } from "@mui/material";
import { useAlert } from "contexts/alert";
import { ArrowBack } from "@mui/icons-material";
import { buildSaveBody, draftFromState, errorMessageOf, saveCampaignPanel } from "./campaigns/api";
import { CONFERIR_MOVIMENTACOES_TEMPLATE_ID } from "./campaigns/types";
import { useCampaignPanel } from "./campaigns/useCampaignPanel";

interface IProps {
  workspace: IWorkspace;
  template: ITemplate;
  handleUpdateTemplate: (payload: ITemplate) => void;
  handleUnselectTemplate: VoidFunction;
  allowedLayouts: {title: string, id: string}[];
}

export function ActivitySetupTemplate({
  workspace,
  template: currentTemplate,
  handleUnselectTemplate,
  handleUpdateTemplate,
  allowedLayouts,
}: IProps) {
  const { openAlert } = useAlert();
  const [template, setTemplate] = useState(() => JSON.parse(JSON.stringify(currentTemplate)) as ITemplate);
  const [isLoading, setIsLoading] = useState(false);
  /** Spec §4.3: no template Conferir Movimentações o app Campanhas salva pela rota própria. */
  const isConferir = template.id === CONFERIR_MOVIMENTACOES_TEMPLATE_ID;
  const campaignPanel = useCampaignPanel(workspace.workspaceId, template.id, isConferir);

  const firstTab = template.activities.some(a => a.type === "RECIPE") ? "RECIPE" : "APP";
  const firstActicity = template.activities.some(a => a.type === "RECIPE") ? template.activities.findIndex(a => a.type === "RECIPE") : template.activities.findIndex(a => a.type === "APP");

  const [tab, setTab] = useState<"RECIPE" | "APP">(firstTab);
  const [currActivity, setCurrActivity] = useState(firstActicity);

  function handleChangeActivity(activityIndex: number, payload: IActivity) {
    console.log("!!!!!!!", payload);
    if (payload.params.editableFields) {
      console.log("🎯 Campos editáveis salvos:", payload.params.editableFields);
    }
    if (payload.params.beneficiaryFields) {
      console.log("🎯 Campos beneficiário salvos:", payload.params.beneficiaryFields);
    }
    const newTemplate = { ...template };
    newTemplate.activities[activityIndex] = { ...payload };

    setTemplate((prev) => ({ ...newTemplate }));
  }

  async function handleSave() {
    try {
      setIsLoading(true);
      
      // Log para verificar o estado do template antes de salvar
      console.log("📤 Salvando template com dados:", template);
      
      // Verificar campos editáveis do Carregar PJs
      const carregarPJsActivity = template.activities.find(activity => activity.name === 'Carregar PJs');
      if (carregarPJsActivity?.params?.editableFields) {
        console.log("📋 Campos editáveis sendo salvos:", carregarPJsActivity.params.editableFields);
      }
      
      // Verificar campos beneficiário do Adicionar beneficiário
      const adicionarBeneficiarioActivity = template.activities.find(activity => activity.name === 'Adicionar beneficiário');
      if (adicionarBeneficiarioActivity?.params?.beneficiaryFields) {
        console.log("📋 Campos beneficiário sendo salvos:", adicionarBeneficiarioActivity.params.beneficiaryFields);
      }
      
      /**
       * why: o app Campanhas de Conferir não passa pelo create-template (forEach sem await, e
       * gravaria os campos PJ/CLT de dados faltantes). A instância nasce pelo backfill ou pelo PUT.
       */
      const templateToCreate = isConferir
        ? { ...template, activities: template.activities.filter((a) => a.name !== "Campanhas") }
        : template;
      await api.post("/v2/workspaces/create-template", {
          workspace,
          template: templateToCreate,
      })
      /** why: sem permissão o PUT responderia 403 e o aviso de erro mentiria sobre o resto, já gravado. */
      if (isConferir && campaignPanel.state && campaignPanel.state.canEdit !== false) {
        try {
          const saved = await saveCampaignPanel(workspace.workspaceId, buildSaveBody(template.id, campaignPanel.state, campaignPanel.draft));
          if (saved.state) {
            campaignPanel.setState(saved.state);
            campaignPanel.setDraft(draftFromState(saved.state));
          } else {
            // Gravou, mas o servidor não conseguiu reler o estado: o painel é relido daqui.
            campaignPanel.reload();
          }
        } catch (campaignError) {
          // O create-template já passou: a tela fica aberta e o rascunho como estava.
          // Aba velha: o backend não gravou nada das campanhas; o painel é relido e o rascunho antigo descartado.
          if ((campaignError as any)?.response?.status === 409) campaignPanel.reload();
          openAlert(`Os demais dados foram salvos, mas as campanhas não: ${errorMessageOf(campaignError, 'Algo deu errado ao salvar as campanhas')}`, 'error')
          return;
        }
      }
      handleUpdateTemplate({...template, isConfigured: true });
      handleUnselectTemplate();
      if (isConferir && !campaignPanel.state) {
        openAlert('Dados salvos. As campanhas não foram salvas porque o painel não carregou.', 'warning')
      } else {
        openAlert('Dados salvos com sucesso', 'success')
      }
    } catch (error) {
      openAlert('Algo deu errado ao salvar os dados', 'error')
    } finally {
      setIsLoading(false);
    }
  }

  function handleChangeTab(tab: "RECIPE" | "APP") {
    if (tab === "APP") {
      const firstActivity = template.activities.find(a => a.type === "APP");
      const firstIndex = template.activities.findIndex(a => a.templateActivityId === firstActivity?.templateActivityId);
      setCurrActivity(firstIndex)
    }
    if (tab === "RECIPE") {
      const firstActivity = template.activities.find(a => a.type === "RECIPE");
      const firstIndex = template.activities.findIndex(a => a.templateActivityId === firstActivity?.templateActivityId);
      setCurrActivity(firstIndex)
    }
    setTab(tab)
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <IconButton onClick={handleUnselectTemplate}>
          <ArrowBack color="primary" />
        </IconButton>
        <Title>Configurar {template.name}</Title>
      </div>
      <div style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ width: "100%", maxWidth: "500px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", gap: "10px" }}>
          {(template.activities.some(a => a.type === "RECIPE") && template.activities.some(a => a.type === "APP")) && (
            <Tabs value={tab} onChange={(_, e) => handleChangeTab(e)} style={{ width: "100%"}}>
              <Tab
                style={{ width: "50%"}}
                label={"Automações"}
                value={"RECIPE"}
              />
              <Tab
                style={{ width: "50%"}}
                label={"Apps"}
                value={"APP"}
              />
            </Tabs>
          )}
          {(template.activities.some(a => a.type === "RECIPE") && !template.activities.some(a => a.type === "APP")) && (
              <Tabs value={tab} onChange={(_, e) => handleChangeTab(e)} style={{ width: "100%"}}>
                <Tab
                  style={{ width: "100%"}}
                  label={"Automações"}
                  value={"RECIPE"}
                />
              </Tabs>
          )}
          {(!template.activities.some(a => a.type === "RECIPE") && template.activities.some(a => a.type === "APP")) && (
              <Tabs value={tab} onChange={(_, e) => handleChangeTab(e)} style={{ width: "100%"}}>
                <Tab
                  style={{ width: "100%", marginLeft: "70px"}}
                  label={"Automações"}
                  value={"APP"}
                />
              </Tabs>
          )}
          <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "8px" }}>
            {template.activities.map((activity, i) => (
              <>
                {activity.type !== tab ? null : (
                  <Bttn active={currActivity === i} onClick={() => setCurrActivity(i)}>{activity.name}</Bttn>
                )}
              </>
            ))}
          </div>
          <ActivitySetupConfig
            index={currActivity}
            allowedLayouts={allowedLayouts}
            activity={template.activities[currActivity]}
            handleChangeActivity={handleChangeActivity}
            campaignPanel={
              isConferir
                ? { state: campaignPanel.state, draft: campaignPanel.draft, onChange: campaignPanel.setDraft, error: campaignPanel.error, onRetry: campaignPanel.reload }
                : undefined
            }
          />
        </div>
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "8px",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          style={{
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            borderRadius: "0.375rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            fontWeight: 600,
            width: "200px",
          }}
          disabled={isLoading}
          onClick={() => handleUnselectTemplate()}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          style={{
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            borderRadius: "0.375rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            fontWeight: 600,
            width: "200px",
          }}
          disabled={isLoading || (isConferir && !campaignPanel.state && !campaignPanel.error)}
          onClick={handleSave}
        >
          Salvar
        </Button>
      </div>
      {/* <button onClick={handleSave}>Salvar</button> */}
    </div>
    // <div>
    //   <button onClick={handleUnselectTemplate}>VOLTAR</button><br></br>
    //   {template.activities.map((activity, i) => <button onClick={() => setCurrActivity(i)}>{activity.name}</button>)}
    //   <ActivitySetupConfig
    //     index={currActivity}
    //     activity={template.activities[currActivity]}
    //     handleChangeActivity={handleChangeActivity}
    //   />
    //   <br></br>
    //   <button onClick={handleSave}>Salvar</button>
    // </div>
  );
}
